import { IconButton, useCarousel } from '@aibees/shared/components';
import { useWindowDimensions } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Breakpoint, CircularProgress, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { mergeSx } from 'merge-sx';
import { memo, useEffect } from 'react';
import { ResponsiveObject } from 'react-slick';
import { MissingData } from '../missing-data';
import * as Styled from './styles';
import * as SX from './sx';
import type { SliderProps as Props } from './types';
import { getSizeForViewport } from './utils';

export const Slider = memo(
  ({
    className,
    slideToShow = {},
    slideToShowDefaultSize,
    slidesToScroll,
    items: elements,
    render,
    before,
    after,
    setting,
    actions,
    actionSx,
    sx,
    headerSx,
    isLoading,
    isBuyingTeamSlider,
    isManagementMatrix,
    showNoDataAvailable,
    title,
    ...cardProps
  }: Props) => {
    const theme = useTheme();
    const { width } = useWindowDimensions();
    const responsive: ResponsiveObject[] = Object.keys(slideToShow).map(
      (k) => ({
        breakpoint: theme.breakpoints.values[k as Breakpoint],

        settings: {
          variableWidth: false,
          slidesToScroll: slidesToScroll ?? 1,
          slidesToShow:
            slideToShow[k as Breakpoint] || slideToShowDefaultSize || 1,
        } as ResponsiveObject['settings'],
      })
    );

    const carousel = useCarousel({
      infinite: false,
      speed: 400,
      ...setting,
      variableWidth: false,
      responsive,
      slidesToShow: slideToShowDefaultSize || 1,
      slidesToScroll: slidesToScroll ?? 1,
    });

    const shouldShowNextButton =
      !!carousel.carouselSettings.slidesToShow &&
      !!elements?.length &&
      elements?.length > 0 &&
      carousel.currentIndex ===
        (elements?.length as number) -
          getSizeForViewport(width, responsive, carousel);

    const lastIndex = Math.round(
      (elements?.length ?? 0) /
        ((carousel.carouselSettings.rows ?? 0) *
          (carousel.carouselSettings.slidesToShow ?? 0))
    );

    useEffect(() => {
      if (carousel.currentIndex !== 0)
        carousel.carouselRef.current?.slickGoTo(0);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [elements]);

    return (
      <Styled.Wrapper
        className={classnames('Slider', className)}
        {...cardProps}
        title={
          typeof title === 'string' ? (
            <Typography variant="body2" fontWeight={600} color="grey.800">
              {title}
            </Typography>
          ) : (
            title
          )
        }
        sx={mergeSx(SX.root, cardProps.cardSx)}
        headerSx={mergeSx(SX.header, headerSx)}
        contentSx={mergeSx(SX.content, cardProps.contentSx)}
        actions={
          <Styled.Actions sx={actionSx}>
            {actions}

            <Stack gap={0.5} direction="row" alignItems="center">
              <IconButton
                size="small"
                variant="outlined"
                onClick={carousel.onPrev}
                disabled={carousel.currentIndex === 0}
                icon="back-fill"
                iconSize={16}
              />
              <IconButton
                size="small"
                variant="outlined"
                onClick={carousel.onNext}
                disabled={
                  (shouldShowNextButton as boolean) ||
                  elements?.length < (slideToShowDefaultSize ?? 0) ||
                  lastIndex === carousel.currentIndex
                }
                icon="forward-fill"
                iconSize={16}
              />
            </Stack>
          </Styled.Actions>
        }
      >
        {before}
        {!isLoading && elements.length === 0 ? (
          // todo THE MESSAGES SHOULD COME FROM
          <MissingData
            message={
              isBuyingTeamSlider
                ? 'No information about teams or employees of this company is available.'
                : showNoDataAvailable
                ? 'No information is available.'
                : isManagementMatrix
                ? 'No Data Available.'
                : 'No similar records are available to display.'
            }
            sx={
              isBuyingTeamSlider || isManagementMatrix
                ? { display: 'flex', justifyContent: 'center' }
                : {}
            }
          />
        ) : (
          <Styled.Slider
            empty={!elements.length}
            ref={carousel.carouselRef}
            {...carousel.carouselSettings}
            sx={{ '.slick-track': { ...sx, textAlign: 'center' } as any }} // eslint-disable-line @typescript-eslint/no-explicit-any
          >
            {elements
              .filter((element) => element.pinned)
              .map((element, i) => render({ ...element, key: i.toString() }))}
            {elements
              .filter((element) => !element.pinned)
              .map((element, i) =>
                render({ ...element, key: (1000 + i).toString() })
              )}
            {isLoading && (
              <CircularProgress
                sx={{ height: '24px!important', width: '24px!important' }}
              />
            )}
          </Styled.Slider>
        )}
        {after}
      </Styled.Wrapper>
    );
  }
);
